import { OpenSiteId, Point } from '../types';
import { EARTH_CIRCUMFERENCE_KM } from './constants';
import { computeDistance, isInBoundingBox } from './location-utils';
import { MapData } from './map-data';

export enum SearchResultType {
  Datasource = 'DATASOURCE',
  OpenSite = 'OPEN_SITE',
  Place = 'PLACE',
}

export const RANKING_WEIGHTS_BY_RESULT_TYPE: Record<SearchResultType, number> = {
  [SearchResultType.Datasource]: 0.5,
  [SearchResultType.OpenSite]: 0.4,
  [SearchResultType.Place]: 0,
};

type BaseSearchResult = {
  label: string;
  type: SearchResultType;
  referencePoint?: Point;
  boundingBox?: number[][];
  location: Point;
  rankingScore: number;
  isOutOfBounds: boolean;
};

export type DatasourceSearchResult = BaseSearchResult & {
  type: SearchResultType.Datasource;
  mapData: MapData;
};

export type OpenSiteSearchResult = BaseSearchResult & {
  type: SearchResultType.OpenSite;
  siteId: OpenSiteId;
};

export type PlaceSearchResult = BaseSearchResult & {
  type: SearchResultType.Place;
};

export type LocationSearchResult = DatasourceSearchResult | OpenSiteSearchResult | PlaceSearchResult;

function computeIsOutOfBounds(boundingBox: number[][] | undefined, location: Point): boolean {
  return boundingBox ? !isInBoundingBox(boundingBox, location.coordinates) : false;
}

function computeRankingScore(
  type: SearchResultType,
  isOutOfBounds: boolean,
  referencePoint: Point | undefined,
  location: Point
): number {
  let score = 1 - Number(isOutOfBounds) + RANKING_WEIGHTS_BY_RESULT_TYPE[type];

  if (referencePoint) {
    const distance = computeDistance(referencePoint.coordinates, location.coordinates);
    score += 1 - distance / EARTH_CIRCUMFERENCE_KM;
  }

  return score;
}

export function createDatasourceSearchResult(mapData: MapData, referencePoint?: Point): DatasourceSearchResult {
  const location = mapData.marker.location;
  const isOutOfBounds = false; // Datasources are always within network bounds
  return {
    label: mapData.datasourceName,
    type: SearchResultType.Datasource,
    referencePoint,
    boundingBox: undefined,
    location,
    mapData,
    isOutOfBounds,
    rankingScore: computeRankingScore(SearchResultType.Datasource, isOutOfBounds, referencePoint, location),
  };
}

export function createOpenSiteSearchResult(
  siteId: OpenSiteId,
  displayName: string,
  location: Point,
  referencePoint?: Point
): OpenSiteSearchResult {
  const isOutOfBounds = false;
  return {
    label: displayName,
    type: SearchResultType.OpenSite,
    referencePoint,
    boundingBox: undefined,
    location,
    siteId,
    isOutOfBounds,
    rankingScore: computeRankingScore(SearchResultType.OpenSite, isOutOfBounds, referencePoint, location),
  };
}

export function createPlaceSearchResult(
  displayName: string,
  location: Point,
  referencePoint?: Point,
  boundingBox?: number[][]
): PlaceSearchResult {
  const isOutOfBounds = computeIsOutOfBounds(boundingBox, location);
  return {
    label: displayName,
    type: SearchResultType.Place,
    referencePoint,
    boundingBox,
    location,
    isOutOfBounds,
    rankingScore: computeRankingScore(SearchResultType.Place, isOutOfBounds, referencePoint, location),
  };
}
